import PopUpMessage from "admin/PopUpMessage";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { AiOutlinePlusCircle } from "react-icons/ai";
import { FaRegEdit } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import { Link } from "react-router-dom";
import CardForm from "./form";
import { IoLogoLinkedin } from "react-icons/io5";
import { toast } from "react-toastify";
import { Icon } from "organisms/iconCommonent";

interface dataProps {
  cover: any;
  fullName: string;
  role: string;
  linkedinLink: string;
  designation?: string;
  description?: string;
  address?: string;
  status?: "enabled" | "disabled";
  _id: string;
}

export default function CardSection({
  role,
  section,
  sectionTitle,
  note,
}: {
  role: string;
  section: string;
  sectionTitle: string;
  note?: string;
}) {
  const [data, setData] = useState<dataProps[]>();
  const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);
  const [brochuresId, setBrochresId] = useState<string>("");
  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [editData, setEditData] = useState<dataProps>();
  const [type, setType] = useState<string>("edit");

  const fetchNewDetails = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_LOCAL_URL}/api/card/show/?role=${role}`
      );
      setData(response.data.data);
    } catch (error: any) {
      console.log(error.message);
    }
  };

  useEffect(() => {
    fetchNewDetails();
  }, []);

  const handleDeleteFromServer = async (id: string) => {
    try {
      await axios.delete(`${process.env.REACT_APP_LOCAL_URL}/api/card/delete/${id}`);
      setData((prevData) => prevData?.filter((obj) => obj._id !== id));
      toast.success("Successfully Deleted", { position: "top-right", autoClose: 3000 });
    } catch (err: any) {
      console.log(err.message);
    }
  };

  const handleFormSubmitEdit = async (formData: any) => {
    try {
      await axios.patch(
        `${process.env.REACT_APP_LOCAL_URL}/api/card/edit/${formData._id}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      handleClose();
      fetchNewDetails();
      toast.success("Updated successfully", { position: "top-right", autoClose: 3000 });
    } catch (error: any) {
      console.log(error.message);
    }
  };

  const handleFormSubmitCreate = async (formData: any) => {
    try {
      await axios.post(`${process.env.REACT_APP_LOCAL_URL}/api/card/create`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      fetchNewDetails();
      handleClose();
      toast.success("Uploaded successfully", { position: "top-right", autoClose: 3000 });
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Error", { position: "top-right", autoClose: 3000 });
    }
  };

  const handleCardDelete = (id: string) => {
    setIsPopupOpen(true);
    setBrochresId(id);
  };

  const handleClose = () => {
    setIsPopupOpen(false);
    setIsFormOpen(false);
  };

  const handleEditFormPopUpOpen = (data: dataProps) => {
    setIsFormOpen(true);
    setEditData(data);
    setType("edit");
  };

  const handleCreateFormData = () => {
    setIsFormOpen(true);
    setType("create");
  };

  // 🔹 New: Toggle card status
  const handleToggleStatus = async (id: string, currentStatus: "enabled" | "disabled") => {
    try {
      const newStatus = currentStatus === "enabled" ? "disabled" : "enabled";
      await axios.patch(`${process.env.REACT_APP_LOCAL_URL}/api/card/toggle-status/${id}`, {
        status: newStatus,
      });
      setData((prev) =>
        prev?.map((item) => (item._id === id ? { ...item, status: newStatus } : item))
      );
      toast.success(`Card ${newStatus}`, { position: "top-right", autoClose: 2000 });
    } catch (error: any) {
      console.log(error.message);
      toast.error("Failed to toggle status", { position: "top-right", autoClose: 2000 });
    }
  };

  return (
    <>
      <div className="blade-top-padding-lg p-4 pt-10">
        <h2 className="font-lato-regular">Section - {section}</h2>
        <hr />
        <div className="flex justify-between items-center mt-10">
          <div className="border-b-[1px] w-full pb-4 flex justify-between">
            <div>
              <h3 className="font-lato-regular">{sectionTitle}</h3>
              <h6 className="font-lato-regular text-red-500">{note}</h6>
            </div>
            <div>
              <button
                className="bg-darkCyan flex items-center text-white font-lato-regular rounded-md px-4 py-2"
                onClick={handleCreateFormData}
              >
                <span className="text-2xl pr-2 relative top-0.5">
                  <Icon icon={AiOutlinePlusCircle} />
                </span>
                <h4 className="text-nowrap">Add new card</h4>
              </button>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-8 mt-8">
          {data?.map((obj: dataProps) => (
            <div className="w-[21rem] mb-8 border-[1px] border-gray-200" key={obj._id}>
              <div className="bg-white h-full flex flex-col justify-between">
                <div>
                  <img
                    className="w-full h-64 object-cover"
                    src={`${process.env.REACT_APP_LOCAL_URL}/${obj.cover.replace(/\\/g, "/")}`}
                    alt={obj.fullName}
                  />
                  <div className="p-4">
                    <h4 className="font-lato-bold text-teal mb-2">{obj.fullName}</h4>
                    <h5 className="font-lato-bold text-black/50 mb-2">{obj.designation}</h5>
                    <h5 className="font-lato-regular text-black mb-2">{obj.address}</h5>
                    <Link
                      to={obj.linkedinLink}
                      target="_blank"
                      className="font-lato-bold text-black/80 underline flex items-center"
                    >
                      <span className="inline-block text-blue-500 text-2xl mr-2">
                        <Icon icon={IoLogoLinkedin} />
                      </span>
                      Linkedin
                    </Link>
                  </div>
                </div>

                <div className="p-4 flex gap-4 flex-col md:flex-row mt-auto">
                  <button
                    className={`px-4 py-2 rounded font-lato-regular ${
                      obj.status === "enabled"
                        ? "bg-teal text-white"
                        : "border-[1px] border-red-500 text-red-500 "
                    }`}
                    onClick={() => handleToggleStatus(obj._id, obj.status || "enabled")}
                  >
                    {obj.status === "enabled" ? "Enabled" : "Disabled"}
                  </button>

                  <button
                    className="bg-blue-500 font-lato-regular text-white px-4 py-2 rounded flex items-center"
                    onClick={() => handleEditFormPopUpOpen(obj)}
                  >
                    <Icon icon={FaRegEdit} /> <span className="ml-1">Edit</span>
                  </button>
                  <button
                    className="text-red-500 font-lato-regular border-[1px] border-red-500 px-4 py-2 rounded flex items-center"
                    onClick={() => handleCardDelete(obj._id)}
                  >
                    <Icon icon={MdDelete} /> <span className="ml-1">Delete</span>
                  </button>
                </div>
              </div>
            </div>
          ))}

          {isPopupOpen && (
            <PopUpMessage
              open={isPopupOpen}
              articleId={brochuresId}
              onClose={handleClose}
              type={"socialcard"}
              onDelete={handleDeleteFromServer}
            />
          )}

          {isFormOpen && type === "edit" && (
            <CardForm
              onSubmit={handleFormSubmitEdit}
              initialData={editData}
              role={role}
              onClose={handleClose}
            />
          )}

          {isFormOpen && type === "create" && (
            <CardForm onSubmit={handleFormSubmitCreate} role={role} onClose={handleClose} />
          )}
        </div>
      </div>
    </>
  );
}
